import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewRef} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ToastService} from "../../../services/Toast.service";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {PROVINCES} from "../../../constants/Provinces.constants";
import {Session} from "../../../services/Session";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {FroalaEditorModule, FroalaViewModule} from "angular-froala-wysiwyg";
import {ModelBasedNumeral} from "../../../directives/model-numeral";
import { environment } from '../../../../environments/environment';
import {PlanCreationGuardService} from '../../../tpiq-services/plan-creation-guard.service';
import {BasicPlanLimitService} from '../../../tpiq-services/basic-plan-limit.service';
import {PlanCreationConfirmation} from '../../../models/basic-plan-limit.model';
import {basicLimitReachedMessage} from '../../../basic-plan-limit/basic-plan-term';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

declare const _: any;

@Component({
  selector: 'app-create-plan',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TpiqSelectDropdownComponent, FroalaEditorModule, FroalaViewModule, ModelBasedNumeral],
  templateUrl: './create-plan.component.html',
  styleUrl: './create-plan.component.scss'
})
export class CreatePlanComponent implements OnInit, OnDestroy {
  @Input() client: any;
  @Output() onCancelCreatePlan: EventEmitter<any> = new EventEmitter<any>();
  @Output() onCreationOfPlan: EventEmitter<any> = new EventEmitter<any>();
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();

  /* Fields for Tax Plan Creation*/
  states: Array<any>;
  allProvinces: Array<any> = PROVINCES.COUNTRY_PROVINCES;
  searchEntries: Array<any> = [];
  newTaxPlan: any = {
    ccropFlatRate: '21',
    marketGrowthRate: '5',
    customTaxRates: []
  };
  showField = true;
  showDesc: boolean;
  editorOptions = {
    heightMin: 100,
    heightMax: 120,
    toolbarButtons: ['bold', 'italic', 'underline', 'fontFamily', 'fontSize', 'color', 'insertLink', 'outdent', 'indent'],
    toolbarSticky: true,
    toolbarVisibleWithoutSelection: false,
    toolbarInline: true,
    fontFamilySelection: true,
    placeholderText: 'Internal notes about this plan',
    charCounterCount: false,
    colorsText: ['#14B5F0', '#3574e3', '#DFCF6A', '#2D62BF', '#09A4DE', '#475E79', '#3474e3', '#102D79', '#434343'],
    events : {},
    key: environment.froalaKey
  };
  searchKey: string;
  years: Array<any> = [];
  currentYear: any;
  creationBlocked = false;
  creationBlockMessage = '';
  creatingPlan = false;
  private creationAvailabilitySequence = 0;
  private readonly createConfirmationCancel$ = new Subject<void>();

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private taxPlanningService: TaxPlanningService,
    private changeDetectorRef: ChangeDetectorRef,
    private planCreationGuard: PlanCreationGuardService,
    private basicPlanLimits: BasicPlanLimitService
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();
    this.currentYear = new Date().getFullYear();
    this.fillYears();
    this.newTaxPlan.clientId = this.client.id;
    this.newTaxPlan.userId = this.client.email;
    this.newTaxPlan.taxYear = new Date().getFullYear();
    this.loadCreationAvailability();
  }

  ngOnDestroy(): void {
    this.createConfirmationCancel$.next();
    this.createConfirmationCancel$.complete();
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  filterStates(stateName) {
    this.states = _.filter(this.allProvinces, (state) => {
      return state.country === stateName;
    });
    this.searchEntries = this.states;
  }

  calculateEffectiveRate(){
    if (this.newTaxPlan.totalTax != 0 && this.newTaxPlan.taxableIncome != 0
      && !isNaN(this.newTaxPlan.totalTax) && !isNaN(this.newTaxPlan.taxableIncome)){
      this.newTaxPlan.effectiveRate = Math.round((this.newTaxPlan.totalTax / this.newTaxPlan.taxableIncome) * 100);
      this.showField = false;
      if (!(this.changeDetectorRef as ViewRef).destroyed) {
        this.changeDetectorRef.detectChanges();
      }
      this.showField = true;
      this.showDesc = true;
    }
  }

  showRatesInfo(){
    window.open('https://taxfoundation.org/data/all/federal/2026-tax-brackets/');
  }

  onHomeStateSelect(state) {
    if (!_.isEmpty(state)) {
      this.newTaxPlan.homeState = state.short;
    }
  }

  showStateRatesInfo(type){
    if (type == 'individual') {
      window.open('https://taxfoundation.org/publications/state-individual-income-tax-rates-and-brackets/');
    } else if (type == 'corporate') {
      window.open('https://taxfoundation.org/publications/state-corporate-income-tax-rates-and-brackets/');
    }
  }

  addCustomTaxRate(){
    this.newTaxPlan.customTaxRates.push({});
  }

  removeCustomTaxRate(index){
    this.newTaxPlan.customTaxRates.splice(index, 1);
  }

  createTaxPlan() {
    if (this.creatingPlan || !this.validateData()){
      return false;
    }
    this.creatingPlan = true;
    const taxYear = Number(this.newTaxPlan.taxYear);
    this.planCreationGuard.confirmCreate(this.org, this.user.id, this.newTaxPlan.clientId, taxYear)
      .pipe(takeUntil(this.createConfirmationCancel$))
      .subscribe({
        next: confirmation => {
          if (!confirmation) {
            this.creatingPlan = false;
            return;
          }
          this.newTaxPlan.id = '';
          this.newTaxPlan.strategyTypes = [{strategies: []}];
          this.newTaxPlan.creationRequestId = confirmation.creationRequestId;
          this.newTaxPlan.confirmedCreditNumber = confirmation.confirmedCreditNumber;
          this.getFormattedCustomRates();
          this.submitTaxPlan(taxYear, confirmation);
        },
        error: error => {
          this.creatingPlan = false;
          this.toastService.pop(TOAST_TYPE.error,
            this.planCreationGuard.errorMessage(error, 'Failed to verify plan entitlement'));
        }
      });
  }

  private submitTaxPlan(taxYear: number, confirmation: PlanCreationConfirmation,
                        countChangeRetryAttempted = false): void {
    this.newTaxPlan.creationRequestId = confirmation.creationRequestId;
    this.newTaxPlan.confirmedCreditNumber = confirmation.confirmedCreditNumber;
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.createTaxPlanForClient(this.org, this.user.id, this.newTaxPlan).subscribe({
      next: (response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.creatingPlan = false;
        this.basicPlanLimits.invalidate(this.org, taxYear);
        this.toastService.pop(TOAST_TYPE.success, 'Tax Plan created successfully');
        this.onCreationOfPlan.emit(response);
      },
      error: (error) => {
        this.loadingService.triggerLoadingEvent(false);
        if (this.planCreationGuard.shouldRetryCountChange(error, countChangeRetryAttempted)) {
          this.planCreationGuard.confirmCreate(this.org, this.user.id, this.newTaxPlan.clientId, taxYear,
            confirmation.creationRequestId).pipe(takeUntil(this.createConfirmationCancel$)).subscribe({
              next: refreshed => {
                if (refreshed) {
                  this.submitTaxPlan(taxYear, refreshed, true);
                } else {
                  this.creatingPlan = false;
                }
              },
              error: refreshError => {
                this.creatingPlan = false;
                this.toastService.pop(TOAST_TYPE.error,
                  this.planCreationGuard.errorMessage(refreshError, 'Failed to verify plan entitlement'));
              }
            });
          return;
        }
        this.creatingPlan = false;
        const message = this.planCreationGuard.errorMessage(error, 'Failed to create tax plan');
        this.toastService.pop(TOAST_TYPE.error, message);
      }
    });
  }

  validateData(){
    const base = this;
    if (!this.newTaxPlan.clientId){
      this.toastService.pop(TOAST_TYPE.error, 'Please select client');
      return false;
    }
    if (!this.newTaxPlan.name){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter Plan name');
      return false;
    }
    if (!this.newTaxPlan.marginalRate){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter Marginal Rate');
      return false;
    }
    if (!this.newTaxPlan.capGainRate){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter Cap Gain Rate');
      return false;
    }
    if (!this.newTaxPlan.ccropFlatRate){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter CCorp Flat Rate');
      return false;
    }
    if (!this.newTaxPlan.taxYear){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter Tax Plan year');
      return false;
    }
    if (!this.newTaxPlan.homeState){
      this.toastService.pop(TOAST_TYPE.error, 'Please select state');
      return false;
    }
    let result = true;
    const customTaxRates = (this.newTaxPlan.customTaxRates || [])
      .filter(rate => !this.isGeneratedStateRate(rate));
    const submittedNames = customTaxRates
      .map(rate => String(rate.name || '').trim().toLowerCase())
      .filter(name => !!name)
      .concat(this.generatedStateRateNames().map(name => name.toLowerCase()));
    if (new Set(submittedNames).size !== submittedNames.length) {
      this.toastService.pop(TOAST_TYPE.error, 'Custom rate names must be unique');
      return false;
    }
    _.each(customTaxRates, (customTaxRate) => {
      if (customTaxRate.name && !customTaxRate.rate){
        base.toastService.pop(TOAST_TYPE.error, `Please enter rate for ${customTaxRate.name}`);
        result = false;
      }
      if (!customTaxRate.name && customTaxRate.rate){
        base.toastService.pop(TOAST_TYPE.error, `Please give name for Tax rate`);
        result = false;
      }
    });
    return result;
  }

  getFormattedCustomRates() {
    const indiStateTaxRate = {name : 'Ind State Rate', rate: this.newTaxPlan.individualStateRate, formula: "{\"actualValue\":\"\",\"displayValue\":\"\",\"displayValuesArray\":[],\"actualValuesArray\":[],\"type\":\"individual\"}"};
    const indiCorporateTaxRate = {name : 'Corp State Rate', rate: this.newTaxPlan.corporateStateRate, formula: "{\"actualValue\":\"\",\"displayValue\":\"\",\"displayValuesArray\":[],\"actualValuesArray\":[],\"type\":\"corporate\"}"};
    if (this.newTaxPlan.homeState) {
      indiStateTaxRate.name = this.newTaxPlan.homeState + ' ' + indiStateTaxRate.name;
      indiCorporateTaxRate.name = this.newTaxPlan.homeState + ' ' + indiCorporateTaxRate.name;
    }
    const customRates = (this.newTaxPlan.customTaxRates || [])
      .filter(rate => !this.isGeneratedStateRate(rate));
    this.newTaxPlan.customTaxRates = [...customRates, indiStateTaxRate, indiCorporateTaxRate];
  }

  private generatedStateRateNames(): string[] {
    const prefix = this.newTaxPlan.homeState ? `${this.newTaxPlan.homeState} ` : '';
    return [`${prefix}Ind State Rate`, `${prefix}Corp State Rate`];
  }

  private isGeneratedStateRate(rate: any): boolean {
    return typeof rate?.formula === 'string'
      && (rate.formula.includes('"type":"individual"') || rate.formula.includes('"type":"corporate"'));
  }

  closeCreatePlanPopup() {
    this.createConfirmationCancel$.next();
    this.creatingPlan = false;
    this.onCancelCreatePlan.emit();
  }

  fillYears(){
    for (let i = this.currentYear - 5; i <= this.currentYear + 5; i++){
      this.years.push({id: i.toString(), name: i.toString()});
    }
  }

  setYear($event) {
    this.newTaxPlan['taxYear'] = $event.id;
    this.loadCreationAvailability(true);
  }

  private loadCreationAvailability(forceRefresh = false): void {
    const loadSequence = ++this.creationAvailabilitySequence;
    const taxYear = Number(this.newTaxPlan.taxYear);
    if (!taxYear || !this.newTaxPlan.clientId) {
      return;
    }
    this.basicPlanLimits.get(this.org, this.user.id, taxYear, this.newTaxPlan.clientId, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: entitlement => {
          if (loadSequence !== this.creationAvailabilitySequence
            || taxYear !== Number(this.newTaxPlan.taxYear)) {
            return;
          }
          this.creationBlocked = entitlement.basicRestrictionsActive && !entitlement.canCreate;
          this.creationBlockMessage = this.creationBlocked
            ? basicLimitReachedMessage(entitlement)
            : '';
        },
        error: () => {
          if (loadSequence !== this.creationAvailabilitySequence
            || taxYear !== Number(this.newTaxPlan.taxYear)) {
            return;
          }
          this.creationBlocked = true;
          this.creationBlockMessage = 'Failed to verify plan creation access. Please try again.';
        }
      });
  }

}
